/** 문제를 열 때만 기존 v2 상세를 읽는다. 검색 목록에 없는 정답·풀이를 별도 요청으로 표시한다. */
import { useEffect, useId, useRef, useState } from 'react';
import { InlineMath } from 'react-katex';
import { useAuth } from '../../context/AuthContext';
import { fetchProblem } from '../ProblemHubV2/problemApi';
import parseAnswerForDisplay from '../OcrHub/answerLatex';
import ProblemBodyPreview from './ProblemBodyPreview';
import { toBodyHtmlMap } from './lexicalHtml';

export default function ProblemAnswerDisclosure({ problemId }) {
  const { getAccessToken } = useAuth();
  const [open, setOpen] = useState(false);
  const [state, setState] = useState({ status: 'idle' });
  const alive = useRef(true);
  const seq = useRef(0);
  const id = useId();
  useEffect(() => {
    alive.current = true;
    setOpen(false);
    setState({ status: 'idle' });
    seq.current += 1;
    return () => { alive.current = false; seq.current += 1; };
  }, [problemId]);
  const load = async () => {
    const n = ++seq.current;
    setState({ status: 'loading' });
    try {
      const d = await fetchProblem(problemId, getAccessToken);
      if (!alive.current || n !== seq.current) return;
      // 변환기는 입력을 변경하므로 응답 원본을 복사해서 넘긴다.
      const raw = d.solution_lexical;
      const html = raw == null ? {} : await toBodyHtmlMap({ solution: typeof raw === 'object' ? JSON.parse(JSON.stringify(raw)) : raw });
      if (!alive.current || n !== seq.current) return;
      setState({ status: 'ok', answer: d.answer, solution: html.solution || null, hasSolution: raw != null });
    } catch (e) {
      if (alive.current && n === seq.current) setState({ status: 'error', message: e.httpStatus === 403 ? '정답·풀이를 볼 권한이 없습니다.' : (e.message || '정답·풀이를 불러오지 못했습니다.') });
    }
  };
  const answer = parseAnswerForDisplay(state.answer);
  return (
    <div className="mt-1.5" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} onDragStart={(e) => e.stopPropagation()}>
      <button type="button" aria-expanded={open} aria-controls={id} onClick={() => { if (!open && state.status === 'idle') load(); setOpen((v) => !v); }} className="rounded-full px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
        {open ? '정답·풀이 접기' : '정답·풀이 보기'}
      </button>
      {open && (
        <div id={id} className="mt-1 space-y-2 rounded-xl border border-gray-200 bg-gray-50 p-2" aria-label={`${problemId}번 문제 정답·풀이`}>
          {state.status === 'loading' && <p role="status" className="text-xs text-gray-500">정답·풀이를 불러오는 중…</p>}
          {state.status === 'error' && <div className="text-xs text-red-600"><p role="alert">{state.message}</p><button type="button" onClick={load} className="mt-1 underline">다시 시도</button></div>}
          {state.status === 'ok' && <>
            <div className="flex items-start gap-2 text-sm"><span className="text-xs text-gray-500">정답</span><span className="min-w-0 overflow-x-auto font-medium text-gray-900">{answer.mode === 'empty' ? <span className="font-normal text-gray-400">정답 없음</span> : answer.mode === 'text' ? answer.value : <InlineMath math={answer.value} renderError={() => <code>{state.answer}</code>} />}</span></div>
            <div><p className="mb-1 text-xs text-gray-500">풀이</p>{state.solution ? <ProblemBodyPreview html={state.solution} /> : <p className="text-xs text-gray-400">{state.hasSolution ? '풀이를 표시하지 못했습니다.' : '등록된 풀이가 없습니다.'}</p>}</div>
          </>}
        </div>
      )}
    </div>
  );
}
